import {
  PlayerEvents,
  PlayerType,
  playerStateManager,
  type PlaybackMode,
  type ActivePodcastOrStation,
} from '@4tn/core-audio-player-shared';
import Media from '@api/models/Media';
import EventDispatcher from '@utils/event-dispatcher';
import TritonPlayer from '../TritonPlayer';
import AudioPlayer from '../AudioPlayer';
import { handleVastPlay } from '../VastPlayer';
import { analyticsMiddleware } from '@middleware/analytics';
import { type IAudioPlayer } from '../AudioPlayer/types';
import { getUriFromMediaObject, isTritonMedia } from './utils';
import { loadSdk as loadTritonSdk } from '@players/TritonPlayer/utils';
import { type ICoreAudioPlayer } from './types';
import { shouldPlayAd, loadSdk as loadVastSdk } from '@players/VastPlayer/utils';
import { type IPlayer, type IConfig } from '../types';
import playerEventHandler from '@players/services/player-event-manager';
import executePoll from '@utils/execute-poll';

class CoreAudioPlayer implements ICoreAudioPlayer {
  private static instance: CoreAudioPlayer | null = null;

  private media: ActivePodcastOrStation;

  private config: IConfig;

  private noPreroll: boolean;

  private _isLoading = false;

  protected player: IPlayer | null = null;

  protected sdksLoaded = false;

  private constructor() {
    void this.loadSdks().then(() => {
      this.sdksLoaded = true;
    });
  }

  public static getInstance(): CoreAudioPlayer {
    if (!CoreAudioPlayer.instance) {
      CoreAudioPlayer.instance = new CoreAudioPlayer();
    }
    return CoreAudioPlayer.instance;
  }

  public initAnalytics(): void {
    // configures UTag
    analyticsMiddleware(EventDispatcher);
  }

  public async play(media: ActivePodcastOrStation, config: IConfig): Promise<void> {
    if (!this.sdksLoaded) {
      await executePoll(() => this.sdksLoaded);
    }
    this.setActivePlayer(media);
    if (this.media) {
      await this.stop();
      await this.handleDestroy(media);
    }

    if (this.media && this.media.id !== media.id) {
      EventDispatcher.sendEvent(PlayerEvents.STREAM_SWITCH, {
        name: PlayerEvents.STREAM_SWITCH,
        player: this.playerType,
        media: new Media(media),
      });
    }

    // track new media and config after previous stream is stopped
    this.media = media;
    this.config = config;
    this._isLoading = true;

    if (!this.config.excludePreroll && shouldPlayAd()) {
      this.noPreroll = true;
      // do not put await in front of this call due to IOS autoplay issue
      return handleVastPlay(this.media, this.config, this.handleStreamPlay.bind(this));
    }
    this.noPreroll = false;
    // do not put await in front of this call due to IOS autoplay issue
    return this.handleStreamPlay();
  }

  public async resume(): Promise<void> {
    if (!this.isReady()) {
      return;
    }
    this._isLoading = true;
    try {
      if (!this.config?.excludePreroll && shouldPlayAd()) {
        await handleVastPlay(this.media, this.config, (this.player as IAudioPlayer).resume.bind(this.player)).finally(
          () => {
            this._isLoading = false;
          }
        );
        return;
      }
      await (this.player as IAudioPlayer).resume().finally(() => {
        this._isLoading = false;
      });
    } catch (error) {
      EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
        name: PlayerEvents.STREAM_ERROR,
      });
      throw error;
    }
  }

  public async stop(): Promise<void> {
    const activePlayer = this.getPlayerByMedia(this.media);
    return activePlayer?.stop().finally(() => {
      this.player = null;
    });
  }

  public pause(): void {
    if (!this.isReady()) {
      return;
    }

    this.player.pause();
  }

  public mute(): void {
    if (!this.isReady()) {
      return;
    }

    this.player.mute();
  }

  public unmute(): void {
    if (!this.isReady()) {
      return;
    }

    this.player.unmute();
  }

  public setVolumeLevel(level: number): void {
    if (!this.isReady()) {
      return;
    }
    playerStateManager.setState({ volumeLevel: level });
    if ((!this.player.volumeLevel || this.player.volumeLevel !== 0) && level === 0) {
      this.player.mute();
    }

    if ((!this.player.volumeLevel || this.player.volumeLevel === 0) && level > 0) {
      this.player.setVolume(level);
      this.player.unmute();
    } else {
      this.player.setVolume(level);
    }
  }

  public seek(intent: number): void {
    if (!this.isReady()) {
      return;
    }
    this.player.seek(intent);
  }

  public isReady(): this is { player: IPlayer; sdksLoaded: boolean } {
    return Boolean(this.player) && this.sdksLoaded;
  }

  private handleDestroy(media: ActivePodcastOrStation): Promise<void> {
    const mediaObject = new Media(media);
    if (isTritonMedia(mediaObject) && AudioPlayer.active) {
      return AudioPlayer.getInstance().destroy();
    }
    return TritonPlayer.getInstance().destroy();
  }

  private getPlayerByMedia(media: ActivePodcastOrStation): IPlayer {
    const mediaObject = new Media(media);
    if (isTritonMedia(mediaObject)) {
      return TritonPlayer.getInstance();
    }
    return AudioPlayer.getInstance();
  }

  private setActivePlayer(media: ActivePodcastOrStation): void {
    const mediaObject = new Media(media);
    if (isTritonMedia(mediaObject)) {
      TritonPlayer.active = true;
      AudioPlayer.active = false;
      return;
    }
    TritonPlayer.active = false;
    AudioPlayer.active = true;
  }

  private handleStreamPlay(): Promise<void> {
    return playerEventHandler(PlayerEvents.STREAM_START, async () => {
      const mediaObject = new Media(this.media);
      const uri = getUriFromMediaObject(mediaObject, this.config);

      if (this.config.volume) {
        playerStateManager.setState({ volumeLevel: this.config.volume });
      }

      if (!uri) {
        const ERROR_MESSAGE = 'Unsupported media format';
        EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
          name: PlayerEvents.STREAM_ERROR,
          player: 'Core Audio Player',
          error: ERROR_MESSAGE,
        });
        throw new Error(ERROR_MESSAGE);
      }

      this.player = this.getPlayerByMedia(this.media);
      return this.player.handlePlay(uri, this.config, mediaObject, this.noPreroll);
    })
      .catch((error) => {
        EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
          name: PlayerEvents.STREAM_ERROR,
          error,
        });
        throw error;
      })
      .finally(() => {
        this._isLoading = false;
      });
  }

  private async loadSdks(): Promise<void> {
    await loadVastSdk();
    await loadTritonSdk();
  }

  get duration(): number | undefined {
    if (!this.isReady()) {
      return;
    }

    return this.player.duration;
  }

  get volume(): number | undefined {
    if (!this.isReady()) {
      return;
    }

    return this.player.volumeLevel;
  }

  get currentTime(): number | undefined {
    if (!this.isReady()) {
      return;
    }

    return this.player.currentTime;
  }

  get isPlaying(): undefined | boolean {
    if (!this.isReady()) {
      return;
    }
    return this.player.isPlaying;
  }

  get isLiveAudio(): undefined | boolean {
    if (!this.isReady()) {
      return;
    }

    return this.player.isLiveAudio;
  }

  get playbackMode(): undefined | PlaybackMode {
    if (!this.isReady()) {
      return;
    }

    return this.player.playbackMode;
  }

  get isMuted(): undefined | boolean {
    if (!this.isReady()) {
      return;
    }

    return this.player.isMuted;
  }

  get playerType(): undefined | PlayerType {
    if (!this.isReady()) {
      return;
    }
    if (TritonPlayer.active) {
      return PlayerType.TRITON;
    }
    return PlayerType.AUDIO;
  }

  get isLoading(): boolean {
    if (!this.isReady()) {
      return false;
    }
    return this._isLoading;
  }
}

const player = CoreAudioPlayer.getInstance();

export { player };
